/* eslint-disable @typescript-eslint/no-explicit-any */
import { DEPARTURE_PORT_FILTER, NIGHTS_LABEL_APPENDER, PROMO_KEY } from 'components/CruiseSearchWidget/constants';
import { FilterOption, Filters, Selection, TypedSelection } from 'components/FiltersProvider/types';
import { addMonths, differenceInYears, endOfMonth, format, startOfMonth, startOfYear } from 'date-fns';
import { Locale } from 'lib/config/config';
import { CalendarType } from '../components/CruiseSearchWidget/types';
import {
	CUSTOM_FILTERS_KEY,
	DateRangeFilter,
	DateRangeFilterMonth,
	DateRangeFilterYear,
	Filter,
	FilterFacet,
	GraphQLDateRangeFilter,
	GraphQLFacetOption,
	GraphQLFilterTypes,
	GraphQLGroupFilter,
	GraphQLListFilter,
	GraphQLNumberRangeFilter,
	GroupListFilter,
	GroupListFilterOption,
	ListFilter,
	ListFilterOption,
} from './apiClient';

export enum DateFormatString {
	fullDate = 'yyyy-MM-dd',
	yearMonth = 'yyyy-MM',
	yearMonthTitle = 'MMMM yyyy',
}

export interface OcupancyFilter {
	key: string;
	type: string;
	label: string;
	isEnabled: boolean;
	options: [
		{
			code: number;
			label: string;
			isDisabled: boolean;
			isSelected: boolean;
		},
	];
}
export interface UpdateSelectionItem {
	value: string[];
	year?: string;
	group?: string;
	calendarType?: CalendarType;
	containers?: Array<string>;
	criteria?: TypedSelection;
}

const numberOfDaysOptions = [
	{
		code: '2~5',
		label: `2 - 5 ${NIGHTS_LABEL_APPENDER}`,
		min: 2,
		max: 5,
	},
	{
		code: '6~8',
		label: `6 - 8 ${NIGHTS_LABEL_APPENDER}`,
		min: 6,
		max: 8,
	},
	{
		code: '9~11',
		label: `9 - 11 ${NIGHTS_LABEL_APPENDER}`,
		min: 9,
		max: 11,
	},
	{
		code: 'gte12',
		label: `12+ ${NIGHTS_LABEL_APPENDER}`,
		min: 2,
	},
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapFilters(graphQLDataFilters: Array<any>, locale: Locale): Array<any> {
	return (
		graphQLDataFilters
			?.filter((filter) => filter['__typename'] !== GraphQLFilterTypes.BOOLEAN)
			.map((grephData) => {
				return mapGraphQlDataFilter(grephData, locale);
			}) ?? []
	);
}

function mapGraphQlDataFilter(graphQlData: any, locale: Locale) {
	switch (graphQlData['__typename']) {
		case GraphQLFilterTypes.LIST:
			return mapListFilter(graphQlData);
		case GraphQLFilterTypes.GROUP_LIST:
			return mapGroupListFilter(graphQlData);
		case GraphQLFilterTypes.NUMBER_RANGE:
			return mapNumberRangeFilter(graphQlData);
		case GraphQLFilterTypes.DATE_RANGE:
			return mapDateRangeFilter(graphQlData, locale?.language);
		default:
			return [];
	}
}

function mapListFilter(graphQLdata: GraphQLListFilter, isEnabled = true) {
	const { key, options } = graphQLdata;
	const filter: ListFilter = {
		key,
		type: GraphQLFilterTypes.LIST,
		label: '',
		isEnabled,
		options: options
			.map(({ code, label, containers, criteria, urlParams, section }) => ({
				code,
				label,
				isDisabled: false,
				isSelected: false,
				containers,
				criteria,
				urlParams,
				section,
			}))
			.sort((a, b) => a.label?.localeCompare(b.label)),
	};

	return filter;
}

function mapGroupListFilter(graphQLdata: GraphQLGroupFilter, isEnabled = true) {
	const { key, groups } = graphQLdata;

	const filter: GroupListFilter = {
		key,
		type: GraphQLFilterTypes.GROUP_LIST,
		label: '',
		isEnabled,
		groups: groups.map(({ code, label, options }) => ({
			code,
			label,
			isDisabled: false,
			isSelected: false,
			options: options
				.map(({ code, label }) => ({
					code,
					label,
					isDisabled: false,
					isSelected: false,
				}))
				.sort((a, b) => a.label?.localeCompare(b.label)),
		})),
	};

	return filter;
}

function mapNumberRangeFilter(graphQLdata: GraphQLNumberRangeFilter, isEnabled = true) {
	const { key } = graphQLdata;

	const filter: ListFilter = {
		key,
		type: GraphQLFilterTypes.NUMBER_RANGE,
		label: '',
		isEnabled,
		options: numberOfDaysOptions
			.map(({ code, label }) => ({
				code,
				label,
				isDisabled: false,
				isSelected: false,
			}))
			.sort((a, b) => a.code?.localeCompare(b.code)),
	};

	return filter;
}

function createUTCDate(dateString: string): Date {
	const [year, month, day] = dateString.split('-').map(Number);
	// Month is 0-indexed in JavaScript Date
	return new Date(Date.UTC(year, month - 1, day));
}

function mapDateRangeFilter(graphQLdata: GraphQLDateRangeFilter, locale: string, isEnabled = true) {
	const { startDate, endDate, key, facets } = graphQLdata;
	const start = createUTCDate(startDate);
	const end = createUTCDate(endDate);
	const yearCount = differenceInYears(end, start) + 1;

	const filter: DateRangeFilter = {
		key,
		label: '',
		type: GraphQLFilterTypes.DATE_RANGE,
		startDate: start,
		endDate: end,
		isEnabled,
		years: mapMonthsYears(startDate, yearCount, facets, locale),
	};

	return filter;
}

function mapMonthsYears(startDateStr: string, yearCount: number, facets: GraphQLFacetOption[], locale: string) {
	const years: DateRangeFilterYear[] = [];

	const [year, month, day] = startDateStr.split('-').map(Number);
	let startDate = startOfYear(new Date(year, month, day));

	for (let i = 0; i < yearCount; i++) {
		const months: DateRangeFilterMonth[] = [];
		const year = format(startDate, 'yyyy');

		for (let j = 0; j < 12; j++) {
			const code = `${year}-${format(startDate, 'MM')}`;
			months.push({
				label: startDate.toLocaleString(locale, { month: 'long' }).substring(0, 3),
				code,
				isDisabled: !facets.some((facet) => facet.code === code),
				isSelected: false,
				dateRange: `${format(startOfMonth(startDate), DateFormatString.fullDate)}~${format(endOfMonth(startDate), DateFormatString.fullDate)}`,
			});
			startDate = addMonths(startDate, 1);
		}
		years.push({
			code: year,
			label: year,
			isDisabled: false,
			isSelected: false,
			months,
		});
	}

	return years;
}

export function getAffectedFilter(filters: Filters, filterKey: string, selectedOptions: Array<string> = []): FilterOption | undefined {
	return filters.find((filter) => {
		if (filterKey === PROMO_KEY) {
			return (
				filter.key === CUSTOM_FILTERS_KEY &&
				Boolean((filter as ListFilter)?.options.find((option) => option.criteria?.promo?.every((promo) => selectedOptions.includes(promo))))
			);
		}

		return filter.key === filterKey;
	});
}

export function updateFilterState(filters: any, filter: string, selectedValue: UpdateSelectionItem): Filters {
	const isDeparturePortFilter = filter === DEPARTURE_PORT_FILTER.key;
	const affectedFilter = getAffectedFilter(filters, filter, selectedValue.value) as any;
	const affectedIndex = filters.indexOf(affectedFilter);
	const { year, group, value, calendarType } = selectedValue;

	if (!affectedFilter) {
		return filters;
	}

	if (affectedFilter.type === GraphQLFilterTypes.LIST || affectedFilter?.type === GraphQLFilterTypes.NUMBER_RANGE) {
		affectedFilter.options.forEach((option: ListFilterOption) => {
			let isOptionFound = false;

			if (filter === PROMO_KEY) {
				isOptionFound = option.isSelected ? value.includes(option.code) : value.every((item) => option?.criteria?.promo?.includes(item));
			} else {
				isOptionFound = value.includes(option.code);
			}

			option.isSelected = isOptionFound ? !option.isSelected : option.isSelected;
		});
		filters[affectedIndex].options = affectedFilter.options;
	}

	if (affectedFilter.type === GraphQLFilterTypes.GROUP_LIST) {
		// Search all groups in case of departure port filter to find duplicated ports (River/Ocean). e.g. AMS
		if (isDeparturePortFilter) {
			affectedFilter.groups.forEach((group: GroupListFilterOption) => {
				group.options.forEach((option: ListFilterOption) => {
					option.isSelected = value.includes(option.code) ? !option.isSelected : option.isSelected;
				});
			});
			filters[affectedIndex].groups = affectedFilter.groups;
		}
		if (!isDeparturePortFilter) {
			affectedFilter.groups
				.find((groupItem: GroupListFilterOption) => groupItem.code === group)
				.options.forEach((option: ListFilterOption) => {
					option.isSelected = value.includes(option.code) ? !option.isSelected : option.isSelected;
				});
			filters[affectedIndex].groups = affectedFilter.groups;
		}
	}

	if (affectedFilter.type === GraphQLFilterTypes.DATE_RANGE && calendarType) {
		switch (calendarType) {
			case CalendarType.Months:
				affectedFilter.years
					.find((yearItem: DateRangeFilterYear) => yearItem.code === year)
					.months.forEach((month: DateRangeFilterMonth) => {
						month.isSelected = value.includes(month.dateRange) ? !month.isSelected : month.isSelected;
					});
				filters[affectedIndex].years = affectedFilter.years;

				break;

			case CalendarType.Dates:
				(affectedFilter as DateRangeFilter).years.forEach((year) => {
					year.months.forEach((month) => (month.isSelected = false));
				});
				break;
		}
	}

	return filters;
}

export function resetFilterState(filters: Filters, filterKey: string): Filters {
	const affectedFilter = filters.find((item: Filter) => item.key === filterKey);
	const affectedIndex = affectedFilter ? filters.indexOf(affectedFilter) : 0;

	if (!affectedFilter) {
		return filters;
	}

	if (affectedFilter?.type === GraphQLFilterTypes.LIST || affectedFilter?.type === GraphQLFilterTypes.NUMBER_RANGE) {
		const listFilter = affectedFilter as ListFilter;

		listFilter.options.forEach((option: ListFilterOption) => {
			option.isSelected = false;
		});
		(filters[affectedIndex] as ListFilter).options = listFilter.options;
	}

	if (affectedFilter?.type === GraphQLFilterTypes.GROUP_LIST) {
		const groupListFilter = affectedFilter as GroupListFilter;

		groupListFilter.groups.forEach((groupItem: GroupListFilterOption) => {
			groupItem.options.forEach((option: ListFilterOption) => {
				option.isSelected = false;
			});
		});
		(filters[affectedIndex] as GroupListFilter).groups = groupListFilter.groups;
	}

	if (affectedFilter?.type === GraphQLFilterTypes.DATE_RANGE) {
		const dateRangeFilter = affectedFilter as DateRangeFilter;

		dateRangeFilter.years.forEach((yearItem: DateRangeFilterYear) => {
			yearItem.months.forEach((month: DateRangeFilterMonth) => {
				month.isSelected = false;
			});
		});
		(filters[affectedIndex] as DateRangeFilter).years = dateRangeFilter.years;
	}

	return filters;
}

export function updateFiltersFacets(filters: Filters, filtersFacets: Array<FilterFacet>): Filters {
	const updatedFilters = [...filters];

	const updateOptionWithFacet = (option: ListFilterOption, facets: GraphQLFacetOption[] = []): ListFilterOption => {
		return {
			...option,
			isDisabled: !option.containers && facets.find((facet) => facet.code === option.code)?.count === 0,
		};
	};

	updatedFilters.forEach((filter) => {
		const { key } = filter;
		const filterFacets = filtersFacets.find((filter: FilterFacet) => filter.key === key) as FilterFacet;

		switch (filter.type) {
			case GraphQLFilterTypes.LIST:
				(filter as ListFilter).options.forEach((option, index, optionsArray) => {
					optionsArray[index] = updateOptionWithFacet(option, filterFacets.facets);
				});
				break;

			case GraphQLFilterTypes.GROUP_LIST:
				(filter as GroupListFilter).groups.forEach(
					(group, index, groupsAray) =>
						(groupsAray[index] = {
							...group,
							options: group.options.map((option) => updateOptionWithFacet(option, filterFacets.facets)),
						}),
				);
				break;

			case GraphQLFilterTypes.NUMBER_RANGE:
				(filter as ListFilter).options.forEach((option) => {
					const [minNights, maxNights] = option.code.split('~');
					if (minNights && maxNights) {
						option.isDisabled =
							filterFacets.facets.filter(
								(facet) => Number(facet.code) >= Number(minNights) && Number(facet.code) <= Number(maxNights) && facet.count > 0,
							).length == 0;
					} else if (minNights && !maxNights) {
						const gte = minNights.replace('gte', '');
						option.isDisabled = filterFacets.facets.filter((facet) => Number(facet.code) >= Number(gte) && facet.count > 0).length == 0;
					}
				});
				break;

			case GraphQLFilterTypes.DATE_RANGE:
				(filter as DateRangeFilter).years.forEach((year) => {
					year.months.forEach((month) => {
						const monthFacet = filterFacets.facets.find((facet) => facet.code === month.code);
						month.isDisabled = !(monthFacet && monthFacet.count > 0);
					});
				});
				break;
			default:
				break;
		}
	});

	return updatedFilters;
}

export function updateAllFiltersState(filters: Filters, selection: Selection): Filters {
	const selectedFilters = Object.keys(selection);
	selectedFilters.forEach((selectedFilter) => {
		const affectedFilter = getAffectedFilter(filters, selectedFilter, selection[selectedFilter]);
		if (!affectedFilter) {
			return;
		}
		if (selection[selectedFilter].length > 0) {
			selection[selectedFilter].forEach((value) => {
				let group: string | undefined;
				let year: string | undefined;
				let calendarType: CalendarType | undefined;

				if (affectedFilter?.type === GraphQLFilterTypes.GROUP_LIST) {
					group = getGroupForFilterValue(affectedFilter as GroupListFilter, value);
				} else if (affectedFilter?.type === GraphQLFilterTypes.DATE_RANGE) {
					({ year, calendarType } = getYearAndCalendarType(affectedFilter as DateRangeFilter, value));
				} else if (affectedFilter.key === CUSTOM_FILTERS_KEY) {
					filters = resetFilterState(filters, selectedFilter);
				}

				filters = updateFilterState(filters, selectedFilter, { value: [value], group, year, calendarType });
			});
		} else {
			filters = resetFilterState(filters, selectedFilter);
		}
	});
	return filters;
}

export function getGroupForFilterValue(filter: GroupListFilter, value: string): string | undefined {
	return filter.groups.find((group: GroupListFilterOption) => group.options.find((option: ListFilterOption) => option.code === value))?.code;
}

export function getYearAndCalendarType(filter: DateRangeFilter, dateRange: string): { year: string | undefined; calendarType: CalendarType } {
	const year = filter.years.find((year: DateRangeFilterYear) =>
		year.months.find((month: DateRangeFilterMonth) => month.dateRange === dateRange),
	)?.code;
	return {
		year,
		calendarType: year ? CalendarType.Months : CalendarType.Dates,
	};
}

export function getContainersForFilterValue(
	filter: ListFilter,
	value: string,
	isPromo: boolean = false,
): Pick<ListFilterOption, 'containers' | 'criteria'> {
	const foundFilter = filter.options.find((option: ListFilterOption) => {
		if (isPromo) {
			return option.criteria?.promo?.includes(value);
		}

		return option.code === value;
	});

	return {
		containers: foundFilter?.containers,
		criteria: foundFilter?.criteria,
	};
}
